/**
 * Provider Map Visibility Page
 * 
 * Dedicated page for managing map visibility settings
 * Updated pricing: Starter €4.99/mo, Boost €9.99/mo
 * 60-day free trial for first 500 providers
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Loader2, MapPin, Zap, TrendingUp,
  CheckCircle, ExternalLink, Home, Gift
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// i18n labels with clear toggle copy - LAUNCH PRICING v2
const LABELS = {
  en: {
    title: 'Live Map Visibility',
    subtitle: 'Control how you appear on the public service map',
    backToDashboard: 'Back to Dashboard',
    currentStatus: 'Current Status',
    hiddenFromMap: 'Hidden from Map',
    visibleOnMap: 'Visible on Map',
    boostedOnMap: 'Boosted on Map',
    toggleLabel: 'Show me on the map',
    toggleDescOn: 'Visible — customers can see and click your profile',
    toggleDescOff: 'Hidden — customers cannot see you on the map',
    toggleTip: 'Tip: turn on to get more requests.',
    boostTitle: 'Boost Your Visibility',
    boostDesc: 'Stand out with exact location, priority placement and a featured badge',
    boostNow: 'Boost Now',
    boostPrice: '€9.99/month',
    boostActive: 'Boost Active',
    boostBenefits: 'Boost Benefits',
    benefitPriority: 'Priority placement in results',
    benefitBadge: 'Featured badge on your pin',
    benefitVisibility: '3x more visibility',
    benefitRanking: 'Higher ranking on the map',
    benefitExactLocation: 'Exact location pin',
    trialActive: 'Trial Active',
    trialDays: 'days remaining',
    noSubscription: 'Not visible on map',
    subscribeNow: 'Get Visible',
    starterPrice: '€4.99/month',
    starterTitle: 'Starter Visible',
    starterBenefits: 'What you get:',
    benefitVisible: 'Visible on public map',
    benefitPin: 'Clickable profile pin (area-level)',
    benefitRequests: 'More customer requests',
    startTrial: 'Start 60-Day Free Trial',
    trialDesc: 'Try map visibility free for 60 days. No credit card required.',
    viewOnMap: 'View on Map',
    loading: 'Loading...',
    or: 'or',
    savedVisible: 'Saved ✓ You are now visible on the map',
    savedHidden: 'Saved ✓ You are now hidden from the map'
  },
  et: {
    title: 'Kaardi nähtavus',
    subtitle: 'Halda oma nähtavust avalikul teenuste kaardil',
    backToDashboard: 'Tagasi töölaudale',
    currentStatus: 'Praegune staatus',
    hiddenFromMap: 'Peidetud kaardilt',
    visibleOnMap: 'Nähtav kaardil',
    boostedOnMap: 'Võimendatud kaardil',
    toggleLabel: 'Näita mind kaardil',
    toggleDescOn: 'Nähtav — kliendid näevad sind kaardil',
    toggleDescOff: 'Peidetud — kliendid ei näe sind kaardil',
    toggleTip: 'Vihje: lülita sisse, et saada rohkem päringuid.',
    boostTitle: 'Võimenda oma nähtavust',
    boostDesc: 'Paista silma täpse asukoha, eelisjärjestuse ja esiletõstetud märgisega',
    boostNow: 'Võimenda kohe',
    boostPrice: '€9.99/kuu',
    boostActive: 'Võimendus aktiivne',
    boostBenefits: 'Võimenduse eelised',
    benefitPriority: 'Eelisjärjestus tulemustes',
    benefitBadge: 'Esiletõstetud märgis nõelal',
    benefitVisibility: '3x rohkem nähtavust',
    benefitRanking: 'Kõrgem positsioon kaardil',
    benefitExactLocation: 'Täpne asukoha nõel',
    trialActive: 'Prooviperiood aktiivne',
    trialDays: 'päeva jäänud',
    noSubscription: 'Pole kaardil nähtav',
    subscribeNow: 'Muutu nähtavaks',
    starterPrice: '€4.99/kuu',
    starterTitle: 'Starter Visible',
    starterBenefits: 'Mida saad:',
    benefitVisible: 'Nähtav avalikul kaardil',
    benefitPin: 'Klõpsatav profiili nõel (piirkonna tasemel)',
    benefitRequests: 'Rohkem klienditaotlusi',
    startTrial: 'Alusta 60-päevast tasuta prooviperioodi',
    trialDesc: 'Proovi kaardi nähtavust 60 päeva tasuta. Krediitkaarti pole vaja.',
    viewOnMap: 'Vaata kaardil',
    loading: 'Laadimine...',
    or: 'või',
    savedVisible: 'Salvestatud ✓ Oled nüüd kaardil nähtav',
    savedHidden: 'Salvestatud ✓ Oled nüüd kaardilt peidetud'
  },
  fi: {
    title: 'Kartan näkyvyys',
    subtitle: 'Hallitse näkyvyyttäsi julkisella palvelukartalla',
    backToDashboard: 'Takaisin hallintapaneeliin',
    currentStatus: 'Nykyinen tila',
    hiddenFromMap: 'Piilotettu kartalta',
    visibleOnMap: 'Näkyvillä kartalla',
    boostedOnMap: 'Tehostettu kartalla',
    toggleLabel: 'Näytä minut kartalla',
    toggleDescOn: 'Näkyvillä — asiakkaat näkevät ja voivat klikata profiiliasi',
    toggleDescOff: 'Piilotettu — asiakkaat eivät näe sinua kartalla',
    toggleTip: 'Vinkki: ota käyttöön saadaksesi enemmän pyyntöjä.',
    boostTitle: 'Tehosta näkyvyyttäsi',
    boostDesc: 'Erotu tarkalla sijainnilla, prioriteettisijoittelulla ja korostetulla merkillä',
    boostNow: 'Tehosta nyt',
    boostPrice: '€9.99/kk',
    boostActive: 'Tehostus aktiivinen',
    boostBenefits: 'Tehostuksen edut',
    benefitPriority: 'Prioriteettisijoittelu tuloksissa',
    benefitBadge: 'Korostettu merkki nastassa',
    benefitVisibility: '3x enemmän näkyvyyttä',
    benefitRanking: 'Korkeampi sijoitus kartalla',
    benefitExactLocation: 'Tarkka sijaintinasta',
    trialActive: 'Kokeilujakso aktiivinen',
    trialDays: 'päivää jäljellä',
    noSubscription: 'Ei näy kartalla',
    subscribeNow: 'Tule näkyväksi',
    starterPrice: '€4.99/kk',
    starterTitle: 'Starter Visible',
    starterBenefits: 'Mitä saat:',
    benefitVisible: 'Näkyvillä julkisella kartalla',
    benefitPin: 'Klikattava profiilinasta (aluetasolla)',
    benefitRequests: 'Enemmän asiakaspyyntöjä',
    startTrial: 'Aloita 60 päivän ilmainen kokeilu',
    trialDesc: 'Kokeile kartan näkyvyyttä ilmaiseksi 60 päivää. Ei luottokorttia.',
    viewOnMap: 'Näytä kartalla',
    loading: 'Ladataan...',
    or: 'tai',
    savedVisible: 'Tallennettu ✓ Olet nyt näkyvillä kartalla',
    savedHidden: 'Tallennettu ✓ Olet nyt piilotettu kartalta'
  },
  sv: {
    title: 'Kartsynlighet',
    subtitle: 'Hantera din synlighet på den offentliga tjänstekartan',
    backToDashboard: 'Tillbaka till instrumentpanelen',
    currentStatus: 'Aktuell status',
    hiddenFromMap: 'Dold från kartan',
    visibleOnMap: 'Synlig på kartan',
    boostedOnMap: 'Boostad på kartan',
    toggleLabel: 'Visa mig på kartan',
    toggleDescOn: 'Synlig — kunder kan se och klicka på din profil',
    toggleDescOff: 'Dold — kunder kan inte se dig på kartan',
    toggleTip: 'Tips: slå på för att få fler förfrågningar.',
    boostTitle: 'Boosta din synlighet',
    boostDesc: 'Stick ut med exakt plats, prioritetsplacering och utmärkt märke',
    boostNow: 'Boosta nu',
    boostPrice: '€9.99/mån',
    boostActive: 'Boost aktiv',
    boostBenefits: 'Boost-fördelar',
    benefitPriority: 'Prioritetsplacering i resultat',
    benefitBadge: 'Utmärkt märke på din nål',
    benefitVisibility: '3x mer synlighet',
    benefitRanking: 'Högre rankning på kartan',
    benefitExactLocation: 'Exakt platsnål',
    trialActive: 'Provperiod aktiv',
    trialDays: 'dagar kvar',
    noSubscription: 'Inte synlig på kartan',
    subscribeNow: 'Bli synlig',
    starterPrice: '€4.99/mån',
    starterTitle: 'Starter Visible',
    starterBenefits: 'Vad du får:',
    benefitVisible: 'Synlig på offentlig karta',
    benefitPin: 'Klickbar profilnål (områdesnivå)',
    benefitRequests: 'Fler kundförfrågningar',
    startTrial: 'Starta 60 dagars gratis provperiod',
    trialDesc: 'Prova kartsynlighet gratis i 60 dagar. Inget kreditkort krävs.',
    viewOnMap: 'Visa på kartan',
    loading: 'Laddar...',
    or: 'eller',
    savedVisible: 'Sparat ✓ Du är nu synlig på kartan',
    savedHidden: 'Sparat ✓ Du är nu dold från kartan'
  },
  nb: {
    title: 'Kartsynlighet',
    subtitle: 'Administrer synligheten din på det offentlige tjenestekartet',
    backToDashboard: 'Tilbake til dashbordet',
    currentStatus: 'Nåværende status',
    hiddenFromMap: 'Skjult fra kartet',
    visibleOnMap: 'Synlig på kartet',
    boostedOnMap: 'Boosted på kartet',
    toggleLabel: 'Vis meg på kartet',
    toggleDescOn: 'Synlig — kunder kan se og klikke på profilen din',
    toggleDescOff: 'Skjult — kunder kan ikke se deg på kartet',
    toggleTip: 'Tips: slå på for å få flere forespørsler.',
    boostTitle: 'Boost synligheten din',
    boostDesc: 'Skill deg ut med eksakt lokasjon, prioritetsplassering og fremhevet merke',
    boostNow: 'Boost nå',
    boostPrice: '€9.99/mnd',
    boostActive: 'Boost aktiv',
    boostBenefits: 'Boost-fordeler',
    benefitPriority: 'Prioritetsplassering i resultater',
    benefitBadge: 'Fremhevet merke på nålen din',
    benefitVisibility: '3x mer synlighet',
    benefitRanking: 'Høyere rangering på kartet',
    benefitExactLocation: 'Eksakt lokasjonsnål',
    trialActive: 'Prøveperiode aktiv',
    trialDays: 'dager igjen',
    noSubscription: 'Ikke synlig på kartet',
    subscribeNow: 'Bli synlig',
    starterPrice: '€4.99/mnd',
    starterTitle: 'Starter Visible',
    starterBenefits: 'Hva du får:',
    benefitVisible: 'Synlig på offentlig kart',
    benefitPin: 'Klikkbar profilnål (områdenivå)',
    benefitRequests: 'Flere kundeforespørsler',
    startTrial: 'Start 60 dagers gratis prøveperiode',
    trialDesc: 'Prøv kartsynlighet gratis i 60 dager. Ingen kredittkort kreves.',
    viewOnMap: 'Vis på kartet',
    loading: 'Laster...',
    or: 'eller',
    savedVisible: 'Lagret ✓ Du er nå synlig på kartet',
    savedHidden: 'Lagret ✓ Du er nå skjult fra kartet'
  },
  ru: {
    title: 'Видимость на карте',
    subtitle: 'Управляйте своей видимостью на публичной карте услуг',
    backToDashboard: 'Назад к панели',
    currentStatus: 'Текущий статус',
    hiddenFromMap: 'Скрыт с карты',
    visibleOnMap: 'Видим на карте',
    boostedOnMap: 'Продвижение на карте',
    toggleLabel: 'Показать меня на карте',
    toggleDescOn: 'Видим — клиенты могут видеть и нажать на ваш профиль',
    toggleDescOff: 'Скрыт — клиенты не видят вас на карте',
    toggleTip: 'Совет: включите, чтобы получать больше запросов.',
    boostTitle: 'Усильте видимость',
    boostDesc: 'Выделитесь с точным местоположением, приоритетным размещением и значком',
    boostNow: 'Усилить сейчас',
    boostPrice: '€9.99/мес',
    boostActive: 'Усиление активно',
    boostBenefits: 'Преимущества усиления',
    benefitPriority: 'Приоритетное размещение в результатах',
    benefitBadge: 'Значок избранного на метке',
    benefitVisibility: 'В 3 раза больше видимости',
    benefitRanking: 'Более высокий рейтинг на карте',
    benefitExactLocation: 'Точная метка местоположения',
    trialActive: 'Пробный период активен',
    trialDays: 'дней осталось',
    noSubscription: 'Не видим на карте',
    subscribeNow: 'Стать видимым',
    starterPrice: '€4.99/мес',
    starterTitle: 'Starter Visible',
    starterBenefits: 'Что вы получаете:',
    benefitVisible: 'Видимость на публичной карте',
    benefitPin: 'Кликабельная метка профиля (уровень района)',
    benefitRequests: 'Больше запросов от клиентов',
    startTrial: 'Начать 60-дневный бесплатный период',
    trialDesc: 'Попробуйте видимость на карте бесплатно 60 дней. Карта не нужна.',
    viewOnMap: 'Посмотреть на карте',
    loading: 'Загрузка...',
    or: 'или',
    savedVisible: 'Сохранено ✓ Вы теперь видимы на карте',
    savedHidden: 'Сохранено ✓ Вы теперь скрыты с карты'
  },
  es: {
    title: 'Visibilidad en el mapa',
    subtitle: 'Controla cómo apareces en el mapa público de servicios',
    backToDashboard: 'Volver al panel',
    currentStatus: 'Estado actual',
    hiddenFromMap: 'Oculto del mapa',
    visibleOnMap: 'Visible en el mapa',
    boostedOnMap: 'Destacado en el mapa',
    toggleLabel: 'Mostrarme en el mapa',
    toggleDescOn: 'Visible — los clientes pueden ver y hacer clic en tu perfil',
    toggleDescOff: 'Oculto — los clientes no pueden verte en el mapa',
    toggleTip: 'Consejo: activa para recibir más solicitudes.',
    boostTitle: 'Potencia tu visibilidad',
    boostDesc: 'Destaca con ubicación exacta, prioridad e insignia destacada',
    boostNow: 'Potenciar ahora',
    boostPrice: '€9.99/mes',
    boostActive: 'Potenciación activa',
    boostBenefits: 'Beneficios de potenciación',
    benefitPriority: 'Ubicación prioritaria en resultados',
    benefitBadge: 'Insignia destacada en tu pin',
    benefitVisibility: '3x más visibilidad',
    benefitRanking: 'Mayor posicionamiento en el mapa',
    benefitExactLocation: 'Pin de ubicación exacta',
    trialActive: 'Período de prueba activo',
    trialDays: 'días restantes',
    noSubscription: 'No visible en el mapa',
    subscribeNow: 'Hacerse visible',
    starterPrice: '€4.99/mes',
    starterTitle: 'Starter Visible',
    starterBenefits: 'Lo que obtienes:',
    benefitVisible: 'Visible en mapa público',
    benefitPin: 'Pin de perfil clicable (nivel de área)',
    benefitRequests: 'Más solicitudes de clientes',
    startTrial: 'Iniciar 60 días de prueba gratis',
    trialDesc: 'Prueba la visibilidad en el mapa gratis por 60 días. Sin tarjeta.',
    viewOnMap: 'Ver en el mapa',
    loading: 'Cargando...',
    or: 'o',
    savedVisible: 'Guardado ✓ Ahora eres visible en el mapa',
    savedHidden: 'Guardado ✓ Ahora estás oculto del mapa'
  }
};

const ProviderMapVisibilityPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const l = LABELS[language] || LABELS.en;
  
  const [loading, setLoading] = useState(true);
  const [mapSettings, setMapSettings] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [activatingTrial, setActivatingTrial] = useState(false);

  useEffect(() => {
    fetchMapSettings();
  }, []);

  const fetchMapSettings = async () => {
    const sessionToken = localStorage.getItem('provider_session');
    if (!sessionToken) {
      navigate('/provider/login');
      return;
    }

    try {
      const [settingsRes, trialRes] = await Promise.all([
        fetch(`${API_URL}/api/map/my/settings`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }),
        fetch(`${API_URL}/api/map/trial/status`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        })
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setMapSettings(data.settings);
      }

      if (trialRes.ok) {
        const data = await trialRes.json();
        setTrialStatus(data);
      }
    } catch (error) {
      console.error('Error fetching map settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (checked) => {
    const sessionToken = localStorage.getItem('provider_session');
    setUpdating(true);

    try {
      const response = await fetch(`${API_URL}/api/map/settings/me`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ map_visible: checked })
      });

      if (response.ok) {
        setMapSettings(prev => ({ ...prev, map_visible: checked }));
        // Clear, localized toast
        toast.success(checked ? l.savedVisible : l.savedHidden);
        
        fetch(`${API_URL}/api/map/analytics?event_type=visibility_toggle_${checked ? 'on' : 'off'}`, {
          method: 'POST'
        });
      }
    } catch (error) {
      toast.error('Failed to update visibility');
    } finally {
      setUpdating(false);
    }
  };

  const handleStartTrial = async () => {
    const sessionToken = localStorage.getItem('provider_session');
    setActivatingTrial(true);

    try {
      const response = await fetch(`${API_URL}/api/map/my/trial/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ categories: [] })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(l.savedVisible);
        fetchMapSettings();
      } else {
        toast.error(data.error || 'Failed to activate trial');
      }
    } catch (error) {
      toast.error('Failed to activate trial');
    } finally {
      setActivatingTrial(false);
    }
  };

  const handleBoostClick = async () => {
    const sessionToken = localStorage.getItem('provider_session');

    try {
      const response = await fetch(`${API_URL}/api/map/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan_id: 'map_boost' })
      });

      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  const handleSubscribeClick = async () => {
    const sessionToken = localStorage.getItem('provider_session');

    try {
      const response = await fetch(`${API_URL}/api/map/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan_id: 'map_basic' })
      });

      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  const getStatusInfo = () => {
    if (!mapSettings) return { status: 'hidden', label: l.hiddenFromMap, color: 'bg-gray-100 text-gray-700' };
    
    const tier = mapSettings.map_tier || 'basic';
    const subscriptionStatus = mapSettings.subscription_status;
    const isVisible = mapSettings.map_visible;

    if (tier === 'boost' || tier === 'premium') {
      return { status: 'boosted', label: l.boostedOnMap, color: 'bg-green-100 text-green-700' };
    }
    
    if (subscriptionStatus === 'active' && isVisible) {
      return { status: 'visible', label: l.visibleOnMap, color: 'bg-blue-100 text-blue-700' };
    }
    
    if (subscriptionStatus === 'trial' && isVisible) {
      return { status: 'trial', label: l.trialActive, color: 'bg-purple-100 text-purple-700' };
    }

    return { status: 'hidden', label: l.hiddenFromMap, color: 'bg-gray-100 text-gray-700' };
  };

  const statusInfo = getStatusInfo();
  const isBoosted = statusInfo.status === 'boosted';
  const canToggle = mapSettings?.subscription_status === 'active' || 
                    mapSettings?.subscription_status === 'trial' ||
                    isBoosted;
  const canStartTrial = !mapSettings?.trial_start_date && mapSettings?.subscription_status !== 'active';
  const isVisible = mapSettings?.map_visible;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        <span className="ml-2 text-gray-600">{l.loading}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-2xl font-bold text-orange-600">KEIKKA</Link>
              <span className="text-gray-300">|</span>
              <span className="text-gray-600 font-medium">{l.title}</span>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/provider/dashboard">
                <Home className="h-4 w-4 mr-1" />
                {l.backToDashboard}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{l.title}</h1>
          <p className="text-gray-600 mt-1">{l.subtitle}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-orange-600" />
                {l.currentStatus}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <Badge className={`text-sm px-3 py-1 ${statusInfo.color}`}>
                  {isBoosted && <Zap className="h-3 w-3 mr-1" />}
                  {statusInfo.label}
                </Badge>
                {trialStatus?.days_remaining > 0 && statusInfo.status === 'trial' && (
                  <span className="text-sm text-purple-600 font-medium">
                    {trialStatus.days_remaining} {l.trialDays}
                  </span>
                )}
              </div>

              {/* Toggle - only if can toggle */}
              {canToggle && (
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{l.toggleLabel}</p>
                      <p className="text-sm text-gray-500">
                        {isVisible ? l.toggleDescOn : l.toggleDescOff}
                      </p>
                    </div>
                    <Switch
                      checked={isVisible || false}
                      onCheckedChange={handleToggleVisibility}
                      disabled={updating}
                      data-testid="map-visibility-toggle"
                    />
                  </div>
                  <p className="text-xs text-orange-600">{l.toggleTip}</p>
                </div>
              )}

              {/* Options when not subscribed */}
              {!canToggle && (
                <div className="space-y-4">
                  {canStartTrial && (
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Gift className="h-5 w-5 text-purple-600" />
                        <span className="font-semibold text-purple-800">{l.startTrial}</span>
                      </div>
                      <p className="text-sm text-purple-700 mb-3">{l.trialDesc}</p>
                      <Button 
                        onClick={handleStartTrial}
                        disabled={activatingTrial}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {activatingTrial && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        {l.startTrial}
                      </Button>
                    </div>
                  )}

                  {canStartTrial && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400 uppercase">{l.or}</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  )}

                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h4 className="font-semibold text-orange-800 mb-2">{l.starterTitle} - {l.starterPrice}</h4>
                    <ul className="text-sm text-orange-600 space-y-1 mb-3">
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> {l.benefitVisible}
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> {l.benefitPin}
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> {l.benefitRequests}
                      </li>
                    </ul>
                    <Button onClick={handleSubscribeClick} className="w-full bg-orange-600 hover:bg-orange-700">
                      {l.subscribeNow} - {l.starterPrice}
                    </Button>
                  </div>
                </div>
              )}

              {isVisible && (
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/map">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {l.viewOnMap}
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Boost Card */}
          <Card className={isBoosted ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50'}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className={`h-5 w-5 ${isBoosted ? 'text-green-600' : 'text-orange-600'}`} />
                {isBoosted ? l.boostActive : l.boostTitle}
              </CardTitle>
              <CardDescription>{l.boostDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2">
                {[l.benefitExactLocation, l.benefitPriority, l.benefitBadge, l.benefitVisibility, l.benefitRanking].filter(Boolean).map((benefit, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className={`h-4 w-4 ${isBoosted ? 'text-green-600' : 'text-orange-600'}`} />
                    {benefit}
                  </li>
                ))}
              </ul>

              {!isBoosted && (
                <Button 
                  onClick={handleBoostClick}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {l.boostNow} - {l.boostPrice}
                </Button>
              )}

              {isBoosted && (
                <div className="p-3 bg-green-100 rounded-lg text-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
                  <p className="text-green-800 font-medium">{l.boostActive}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ProviderMapVisibilityPage;
